import { Link } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { ArrowRight, CheckCircle } from "lucide-react";
import { Helmet } from "react-helmet-async";

const IMAGE = "https://customer-assets.emergentagent.com/job_cayenne-processor/artifacts/4u5e08ja_IMG-20260415-WA0036.jpg";

export default function DriedPineapplePage() {
  return (
    <main data-testid="dried-pineapple-page" className="pt-20">
      <Helmet>
        <title>Dried Pineapple | Novapine | Wholesale Supplier South Africa</title>
        <meta name="description" content="Buy dried pineapple in bulk from Novapine, Hluhluwe. Preserved dried Cayenne pineapple from Mkonge Farm. Ideal for snack manufacturers, trail mix producers, health food brands, and retailers across South Africa." />
        <meta name="keywords" content="dried pineapple, dried pineapple supplier South Africa, bulk dried pineapple, dried fruit wholesale, preserved pineapple, dried pineapple snack, Cayenne pineapple dried, Novapine, Mkonge Farm, Hluhluwe" />
        <link rel="canonical" href="https://www.novapine.co.za/products/dried-pineapple" />
        <meta property="og:title" content="Dried Pineapple | Novapine | Wholesale Supplier South Africa" />
        <meta property="og:description" content="Buy dried pineapple in bulk. Preserved Cayenne pineapple from Mkonge Farm. For snack brands, trail mix, health food, and retail." />
        <meta property="og:image" content={IMAGE} />
      </Helmet>

      <section className="py-20 bg-novapine-brown">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="font-heading text-xs uppercase tracking-[0.2em] font-semibold text-novapine-gold mb-4">Product</p>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tighter text-white mb-4">Dried Pineapple</h1>
          <p className="font-body text-base text-white/70 max-w-lg mx-auto">Preserved dried Cayenne pineapple from Mkonge Farm, Hluhluwe.</p>
        </div>
      </section>

      <section className="py-24 bg-[#F9F6F0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <img src={IMAGE} alt="Dried pineapple production at Novapine" className="rounded-xl w-full h-[450px] object-cover" />
            </div>
            <div>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-novapine-brown mb-6">Wholesale Dried Pineapple</h2>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-6">
                Our dried pineapple is produced through controlled dehydration of fresh Cayenne pineapples and preserved for long shelf life. Processed at our facility in Hluhluwe, KwaZulu-Natal, from pineapples grown on Mkonge Farm.
              </p>
              <p className="font-body text-base text-novapine-brown/70 leading-relaxed mb-8">
                A versatile, shelf-stable product that retains the rich flavour of fresh Cayenne pineapple. Available in bulk for snack manufacturers, health food brands, ingredient suppliers, and retailers.
              </p>
              <h3 className="font-heading font-semibold text-sm uppercase tracking-[0.1em] text-novapine-gold mb-4">Applications</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-8">
                {["Snack food manufacturing", "Trail mix and granola", "Cereal and health bar ingredients", "Confectionery and baking", "Retail packaged dried fruit", "Home snacking"].map((app) => (
                  <div key={app} className="flex items-center gap-2">
                    <CheckCircle size={14} className="text-novapine-gold shrink-0" />
                    <span className="font-body text-sm text-novapine-brown/70">{app}</span>
                  </div>
                ))}
              </div>
              <Link to="/contact">
                <Button className="btn-gold rounded-md px-8 py-5 font-heading text-sm tracking-wide font-semibold">
                  Enquire About Dried Pineapple <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl tracking-tight text-novapine-brown mb-6">Why Choose Novapine Dried Pineapple?</h2>
          <div className="space-y-4">
            <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
              <strong className="text-novapine-brown">Long shelf life.</strong> Our dried pineapple is preserved and shelf-stable, making it ideal for retail, export, and long supply chains.
            </p>
            <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
              <strong className="text-novapine-brown">Versatile product.</strong> Available in slices, rings, and diced formats to suit your production needs — from trail mix to baked goods.
            </p>
            <p className="font-body text-base text-novapine-brown/70 leading-relaxed">
              <strong className="text-novapine-brown">Single-origin quality.</strong> Every batch is traceable to Mkonge Farm, Hluhluwe — ensuring consistent flavour and quality.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 bg-novapine-brown">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white mb-6">Ready to Order Dried Pineapple?</h2>
          <p className="font-body text-base text-white/70 mb-10">Contact our team to discuss volumes, pricing, and delivery.</p>
          <Link to="/contact"><Button className="btn-gold rounded-md px-10 py-6 font-heading text-sm tracking-wide font-semibold">Get In Touch <ArrowRight size={16} className="ml-2" /></Button></Link>
        </div>
      </section>
    </main>
  );
}
